import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Loader2, MapPin, Save, Move, RefreshCw, Maximize2, Package2, Warehouse, Truck, AlertTriangle, Wrench, Building2 } from "lucide-react";
import { toast } from "sonner";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLOORPLAN_URL = "https://customer-assets.emergentagent.com/job_89a536dc-36dd-4821-ba4f-a6c069ebdb60/artifacts/qu52sgcs_5fde25a3-c8c4-47bc-bd26-752809397455.jpeg";

// Posições sugeridas por tipo de máquina (percentagens sobre a imagem)
// Se houver mais máquinas do que slots num tipo, faz wrap para linha abaixo
const SLOTS_BY_TYPE = {
  injection: [
    { x: 15, y: 60 }, { x: 29, y: 60 }, { x: 44, y: 60 },
    { x: 15, y: 72 }, { x: 29, y: 72 }, { x: 44, y: 72 },
  ],
  montagem: [
    { x: 52, y: 40 }, { x: 62, y: 40 }, { x: 52, y: 52 }, { x: 62, y: 52 },
  ],
  serigrafia: [
    { x: 65, y: 25 }, { x: 72, y: 25 },
  ],
  tampografia: [
    { x: 55, y: 25 }, { x: 62, y: 25 },
  ],
  extrusion: [
    { x: 40, y: 25 }, { x: 48, y: 25 },
  ],
  blow: [
    { x: 30, y: 25 }, { x: 36, y: 25 },
  ],
  other: [
    { x: 20, y: 30 }, { x: 25, y: 30 }, { x: 20, y: 42 }, { x: 25, y: 42 },
  ],
};

function pickDefaultSlot(machineType, indexInType) {
  const slots = SLOTS_BY_TYPE[machineType] || SLOTS_BY_TYPE.other;
  return slots[indexInType % slots.length];
}

// Zonas clicáveis da planta (em % da imagem)
const CLICKABLE_ZONES = [
  { key: "warehouse", label: "Armazém MP", icon: Warehouse, color: "amber", left: 2, top: 78, width: 22, height: 20 },
  { key: "finished",  label: "Racks Produto Acabado", icon: Package2, color: "emerald", left: 78, top: 18, width: 22, height: 55 },
  { key: "shipping",  label: "Carga / Descarga", icon: Truck, color: "blue", left: 30, top: 82, width: 45, height: 16 },
  { key: "assembly",  label: "Zona de Montagem", icon: Wrench, color: "purple", left: 47, top: 35, width: 28, height: 22 },
  { key: "offices",   label: "Escritórios", icon: Building2, color: "slate", left: 2, top: 2, width: 55, height: 16 },
];

const STATUS_META = {
  running: { color: "#10b981", ring: "ring-emerald-500", bg: "bg-emerald-500", label: "Em execução", pulse: true },
  paused:  { color: "#dc2626", ring: "ring-red-600",     bg: "bg-red-600",     label: "Parada",       pulse: false },
  delayed: { color: "#f59e0b", ring: "ring-amber-500",   bg: "bg-amber-500",   label: "Atrasada",     pulse: true },
  idle:    { color: "#94a3b8", ring: "ring-slate-400",   bg: "bg-slate-400",   label: "Livre",        pulse: false },
};

export default function FactoryMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [positions, setPositions] = useState({});
  const [zones, setZones] = useState({}); // { warehouse: {left,top,width,height}, ... }
  const [dragging, setDragging] = useState(null);       // id da máquina a arrastar
  const [zoneDragging, setZoneDragging] = useState(null); // {key, mode: 'move'|'resize', startX, startY, origLeft, origTop, origW, origH}
  const [selected, setSelected] = useState(null);
  const [zoneOpen, setZoneOpen] = useState(null); // 'warehouse' | 'finished' | 'shipping'
  const [zoneData, setZoneData] = useState(null);
  const [zoneLoading, setZoneLoading] = useState(false);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const openZone = async (zoneKey) => {
    if (editing) return;
    setZoneOpen(zoneKey);
    setZoneLoading(true);
    setZoneData(null);
    try {
      const r = await axios.get(`${API}/factory-map/zone-stats/${zoneKey}`);
      setZoneData(r.data);
    } catch (e) {
      showError(e, "Erro a carregar zona");
      setZoneOpen(null);
    } finally {
      setZoneLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/factory-map`);
      setData(r.data);
      const p = {};
      // Agrupar por tipo para atribuir slots correctos
      const countByType = {};
      r.data.machines.forEach(m => {
        // "Default" = posição não personalizada pelo utilizador. Backend usa (10,10) ou (50,50) como defaults.
        const isDefault = m.x == null || m.y == null || (m.x === 10 && m.y === 10) || (m.x === 50 && m.y === 50);
        if (isDefault) {
          const t = m.machine_type || "other";
          const idx = countByType[t] || 0;
          countByType[t] = idx + 1;
          const slot = pickDefaultSlot(t, idx);
          p[m.id] = { x: slot.x, y: slot.y, zone: m.zone };
        } else {
          p[m.id] = { x: m.x, y: m.y, zone: m.zone };
        }
      });
      setPositions(p);
      // Zonas: usa as vindas do backend, cai para defaults se não existirem
      const zonesFromApi = r.data.zones || {};
      const z = {};
      CLICKABLE_ZONES.forEach(cz => {
        const saved = zonesFromApi[cz.key];
        z[cz.key] = saved ? { left: saved.left, top: saved.top, width: saved.width, height: saved.height }
                          : { left: cz.left, top: cz.top, width: cz.width, height: cz.height };
      });
      setZones(z);
    } catch (e) { showError(e, "Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const iv = setInterval(() => { if (!editing) load(); }, 30000); return () => clearInterval(iv); }, [editing]);

  const getEventPos = (e) => {
    if (!canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches?.[0] || e;
    return {
      x: ((src.clientX - rect.left) / rect.width) * 100,
      y: ((src.clientY - rect.top) / rect.height) * 100,
    };
  };

  const onCanvasMouseMove = (e) => {
    const p = getEventPos(e);
    if (!p) return;

    if (dragging) {
      setPositions(prev => ({ ...prev, [dragging]: { ...(prev[dragging] || {}), x: Math.max(0, Math.min(96, p.x)), y: Math.max(0, Math.min(94, p.y)) } }));
      return;
    }
    if (zoneDragging) {
      const { key, mode, startX, startY, origLeft, origTop, origW, origH } = zoneDragging;
      const dx = p.x - startX;
      const dy = p.y - startY;
      setZones(prev => {
        const cur = prev[key] || {};
        if (mode === "move") {
          return { ...prev, [key]: { ...cur,
            left: Math.max(0, Math.min(100 - origW, origLeft + dx)),
            top: Math.max(0, Math.min(100 - origH, origTop + dy)),
          } };
        }
        return { ...prev, [key]: { ...cur,
          width: Math.max(5, Math.min(100 - origLeft, origW + dx)),
          height: Math.max(5, Math.min(100 - origTop, origH + dy)),
        } };
      });
    }
  };
  const onMouseUp = () => { setDragging(null); setZoneDragging(null); };

  const savePositions = async () => {
    try {
      const payload = Object.entries(positions).map(([machine_id, pos]) => ({
        machine_id, x: pos.x, y: pos.y, zone: pos.zone || null,
      }));
      await axios.put(`${API}/factory-map/positions`, payload);
      // Guardar zonas também
      await axios.put(`${API}/factory-map/zones`, zones);
      toast.success("Posições e zonas guardadas");
      setEditing(false);
      load();
    } catch (e) { showError(e, "Erro a guardar"); }
  };

  if (loading && !data) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" /></div>;

  const stats = data?.machines.reduce((acc, m) => { acc[m.status] = (acc[m.status] || 0) + 1; return acc; }, {}) || {};

  return (
    <div className="p-6 space-y-4" data-testid="factory-map-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-2"><MapPin className="h-7 w-7 text-blue-600" />Mapa da Fábrica</h1>
          <p className="text-sm text-muted-foreground mt-1">Estado das máquinas em tempo real · Actualizado {new Date(data?.as_of).toLocaleTimeString("pt-PT")}</p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => { setEditing(false); load(); }}>Cancelar</Button>
              <Button onClick={savePositions} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-positions"><Save className="h-4 w-4 mr-2" />Guardar Posições</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={load}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-map"><Move className="h-4 w-4 mr-2" />Editar Posições</Button>
            </>
          )}
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-2 text-xs">
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <div key={key} className="flex items-center gap-2 p-3 bg-white border rounded-lg">
            <div className={`h-4 w-4 rounded-full ${meta.bg}`}></div>
            <div>
              <div className="font-bold text-lg">{stats[key] || 0}</div>
              <div className="text-slate-500">{meta.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Canvas */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div
            ref={canvasRef}
            onMouseMove={onCanvasMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onTouchMove={onCanvasMouseMove}
            onTouchEnd={onMouseUp}
            className={`relative w-full border-2 ${editing ? "border-blue-500 border-dashed" : "border-slate-200"} touch-none`}
            style={{
              aspectRatio: "1/1",
              backgroundImage: `url(${FLOORPLAN_URL})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
            data-testid="factory-canvas"
          >
            {/* Overlay branco muito subtil para as máquinas destacarem */}
            <div className="absolute inset-0 bg-white/5 pointer-events-none"></div>

            {/* Zonas clicáveis (edit mode: draggable + resizable; view mode: click abre modal) */}
            {CLICKABLE_ZONES.map(z => {
              const Icon = z.icon;
              const colorMap = {
                amber: { hover: "hover:bg-amber-400/30 hover:border-amber-500", border: "border-amber-500/40", text: "text-amber-800" },
                emerald: { hover: "hover:bg-emerald-400/30 hover:border-emerald-500", border: "border-emerald-500/40", text: "text-emerald-800" },
                blue: { hover: "hover:bg-blue-400/30 hover:border-blue-500", border: "border-blue-500/40", text: "text-blue-800" },
                purple: { hover: "hover:bg-purple-400/30 hover:border-purple-500", border: "border-purple-500/40", text: "text-purple-800" },
                slate: { hover: "hover:bg-slate-400/30 hover:border-slate-500", border: "border-slate-500/40", text: "text-slate-800" },
              };
              const cur = zones[z.key] || { left: z.left, top: z.top, width: z.width, height: z.height };
              const isWarehouse = z.key === "warehouse";
              const stockSummary = data?.warehouse_summary;
              return (
                <div
                  key={z.key}
                  className={`absolute rounded-lg border-2 ${editing ? `${colorMap[z.color].border} bg-white/40 border-dashed cursor-move` : `border-transparent bg-transparent ${colorMap[z.color].hover} cursor-pointer group`} transition-all duration-200`}
                  style={{ left: `${cur.left}%`, top: `${cur.top}%`, width: `${cur.width}%`, height: `${cur.height}%` }}
                  onMouseDown={(e) => {
                    if (!editing) return;
                    // start move
                    if (!canvasRef.current) return;
                    const rect = canvasRef.current.getBoundingClientRect();
                    const startX = ((e.clientX - rect.left) / rect.width) * 100;
                    const startY = ((e.clientY - rect.top) / rect.height) * 100;
                    setZoneDragging({ key: z.key, mode: "move", startX, startY,
                      origLeft: cur.left, origTop: cur.top, origW: cur.width, origH: cur.height });
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    if (!editing || !canvasRef.current) return;
                    const t = e.touches[0];
                    const rect = canvasRef.current.getBoundingClientRect();
                    const startX = ((t.clientX - rect.left) / rect.width) * 100;
                    const startY = ((t.clientY - rect.top) / rect.height) * 100;
                    setZoneDragging({ key: z.key, mode: "move", startX, startY,
                      origLeft: cur.left, origTop: cur.top, origW: cur.width, origH: cur.height });
                    e.stopPropagation();
                  }}
                  onClick={() => { if (!editing) openZone(z.key); }}
                  data-testid={`zone-${z.key}`}
                  title={editing ? `Arrastar / redimensionar: ${z.label}` : `Ver detalhes: ${z.label}`}
                >
                  {/* Etiqueta em modo edição sempre visível; em modo view aparece no hover */}
                  <div className={`absolute top-1 left-1 flex items-center gap-1 bg-white/95 px-2 py-1 rounded shadow-md text-xs font-bold ${editing ? "" : "opacity-0 group-hover:opacity-100 transition-opacity"}`}>
                    <Icon className="h-3 w-3" /> {z.label}
                  </div>

                  {/* Badge sempre visível para warehouse (stock actual) */}
                  {!editing && isWarehouse && stockSummary && (
                    <div className={`absolute bottom-1 left-1 right-1 pointer-events-none flex flex-col items-start gap-0.5`}>
                      <div className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow flex items-center gap-1">
                        <Warehouse className="h-3 w-3" />
                        {stockSummary.total_stock_kg} kg
                      </div>
                      {stockSummary.low_stock_count > 0 && (
                        <div className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow flex items-center gap-1 animate-pulse">
                          <AlertTriangle className="h-3 w-3" />
                          {stockSummary.low_stock_count} em rutura
                        </div>
                      )}
                    </div>
                  )}

                  {/* Alça de redimensionamento (canto inferior-direito) — só em edit */}
                  {editing && (
                    <div
                      className="absolute -bottom-1.5 -right-1.5 w-4 h-4 bg-white border-2 border-slate-600 rounded-sm cursor-nwse-resize shadow"
                      onMouseDown={(e) => {
                        if (!canvasRef.current) return;
                        const rect = canvasRef.current.getBoundingClientRect();
                        const startX = ((e.clientX - rect.left) / rect.width) * 100;
                        const startY = ((e.clientY - rect.top) / rect.height) * 100;
                        setZoneDragging({ key: z.key, mode: "resize", startX, startY,
                          origLeft: cur.left, origTop: cur.top, origW: cur.width, origH: cur.height });
                        e.stopPropagation();
                      }}
                      data-testid={`zone-resize-${z.key}`}
                    />
                  )}
                </div>
              );
            })}
            {editing && (
              <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-3 py-1.5 rounded shadow z-10">
                Arraste as máquinas para as suas posições reais
              </div>
            )}

            {/* Machines (z-30 para ficarem acima das zonas em modo editar) */}
            {(data?.machines || []).map(m => {
              const pos = positions[m.id] || { x: m.x, y: m.y };
              const meta = STATUS_META[m.status] || STATUS_META.idle;
              return (
                <div
                  key={m.id}
                  onMouseDown={editing ? (e) => { e.stopPropagation(); setDragging(m.id); } : undefined}
                  onTouchStart={editing ? (e) => { e.stopPropagation(); setDragging(m.id); } : undefined}
                  onClick={() => !editing && setSelected(m)}
                  className={`absolute select-none z-30 ${editing ? "cursor-move" : "cursor-pointer"} group ${dragging === m.id ? "scale-110 z-40" : ""}`}
                  style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -50%)" }}
                  data-testid={`map-machine-${m.code}`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <div className={`relative w-14 h-14 rounded-lg shadow-2xl flex items-center justify-center text-white font-bold text-xs ring-2 ring-white/90 outline outline-2 outline-black/40 ${meta.pulse ? "animate-pulse" : ""}`}
                         style={{ backgroundColor: meta.color }}>
                      {m.code}
                      {m.machine_type && m.machine_type !== "injection" && (
                        <div className="absolute -top-1 -left-1 bg-slate-800 text-white text-[8px] px-1 py-0.5 rounded shadow font-bold uppercase leading-none tracking-wide" title={m.machine_type}>
                          {m.machine_type.slice(0, 3)}
                        </div>
                      )}
                      {m.current_order && (
                        <div className="absolute -bottom-1 -right-1 bg-white text-slate-800 text-[9px] px-1 py-0.5 rounded shadow font-bold border">
                          {Math.round(m.progress)}%
                        </div>
                      )}
                    </div>
                    <div className="bg-slate-900/85 text-white px-2 py-0.5 rounded text-[10px] font-semibold shadow-lg border border-white/20 max-w-[110px] truncate">
                      {m.current_order?.code || meta.label}
                    </div>
                  </div>
                </div>
              );
            })}
            {data?.machines.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                Sem máquinas configuradas. Adicione máquinas em <strong className="ml-1">Engenharia → Máquinas</strong>.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Machine detail popover */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg shadow flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: STATUS_META[selected.status]?.color }}>
                {selected.code}
              </div>
              <div>
                <div className="font-bold text-lg">{selected.code}</div>
                <div className="text-xs text-slate-500">{selected.description}</div>
              </div>
              <span className={`ml-auto text-xs font-bold uppercase px-2 py-1 rounded text-white`} style={{ backgroundColor: STATUS_META[selected.status]?.color }}>
                {STATUS_META[selected.status]?.label}
              </span>
            </div>
            {selected.current_order && (
              <div className="border-t pt-3 space-y-1 text-sm">
                <div><span className="text-slate-500">Ordem:</span> <strong>{selected.current_order.code}</strong></div>
                <div><span className="text-slate-500">Produto:</span> <strong>{selected.current_order.part_code || "-"}</strong></div>
                <div><span className="text-slate-500">Progresso:</span> <strong className="text-emerald-600">{Math.round(selected.progress)}%</strong> ({selected.current_order.current_good_quantity || 0}/{selected.current_order.good_quantity_target || 0})</div>
              </div>
            )}
            {selected.model && <div className="text-xs text-slate-500 mt-2">Modelo: {selected.model} {selected.tonnage ? `· ${selected.tonnage} T` : ""}</div>}
            <Button onClick={() => setSelected(null)} className="w-full mt-4">Fechar</Button>
          </div>
        </div>
      )}

      {/* Modal de Zona */}
      <Dialog open={!!zoneOpen} onOpenChange={(o) => { if (!o) { setZoneOpen(null); setZoneData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="zone-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {zoneOpen === "warehouse" && <Warehouse className="h-5 w-5 text-amber-600" />}
              {zoneOpen === "finished" && <Package2 className="h-5 w-5 text-emerald-600" />}
              {zoneOpen === "shipping" && <Truck className="h-5 w-5 text-blue-600" />}
              {zoneOpen === "assembly" && <Wrench className="h-5 w-5 text-purple-600" />}
              {zoneOpen === "offices" && <Building2 className="h-5 w-5 text-slate-600" />}
              {zoneData?.title || "Zona"}
            </DialogTitle>
          </DialogHeader>
          {zoneLoading && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>}

          {zoneOpen === "warehouse" && zoneData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Materiais</div><div className="text-2xl font-bold">{zoneData.total_materials}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Stock total</div><div className="text-2xl font-bold">{zoneData.total_stock_kg} kg</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Consumido hoje</div><div className="text-2xl font-bold text-blue-700">{zoneData.consumed_today_kg} kg</div></CardContent></Card>
                <Card className={zoneData.low_stock_count > 0 ? "border-red-300 bg-red-50" : ""}>
                  <CardContent className="pt-4"><div className="text-xs text-gray-500">Rutura</div><div className={`text-2xl font-bold ${zoneData.low_stock_count > 0 ? "text-red-700" : "text-emerald-700"}`}>{zoneData.low_stock_count}</div></CardContent>
                </Card>
              </div>
              {zoneData.low_stock_count > 0 && (
                <div>
                  <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Alertas de Rutura</h4>
                  <div className="space-y-1">
                    {zoneData.low_stock_items.map((it, i) => (
                      <div key={i} className="flex justify-between items-center p-2 bg-red-50 border border-red-200 rounded text-sm">
                        <span><span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded mr-2">{it.code}</span>{it.name || "-"}</span>
                        <span className="text-red-700 font-bold">{it.stock_kg}kg / mín {it.min_kg}kg</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Top 5 Stock</h4>
                <div className="space-y-1">
                  {zoneData.materials_top.map((m, i) => (
                    <div key={i} className="flex justify-between items-center p-2 border rounded text-sm">
                      <span><span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded mr-2">{m.code}</span>{m.name || "-"}</span>
                      <span className="font-bold text-slate-700">{m.stock_kg} kg</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {zoneOpen === "finished" && zoneData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Ordens prontas</div><div className="text-2xl font-bold">{zoneData.orders_ready}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Paletes</div><div className="text-2xl font-bold text-indigo-700">{zoneData.total_pallets}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Caixas</div><div className="text-2xl font-bold text-emerald-700">{zoneData.total_boxes}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Etiquetas hoje</div><div className="text-2xl font-bold">{zoneData.labels_generated_today}</div></CardContent></Card>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Prontas a Expedir</h4>
                {zoneData.orders.length === 0 ? <p className="text-sm italic text-gray-500">Sem ordens em espera de expedição.</p> :
                  <div className="space-y-1">
                    {zoneData.orders.map((o, i) => (
                      <div key={i} className="flex justify-between items-center p-2 border rounded text-sm">
                        <div>
                          <div className="font-bold">{o.code} <span className="text-xs text-gray-500 ml-2">{o.part_code}</span></div>
                          <div className="text-xs text-gray-500">{o.client_name}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right text-xs">
                            <div className="font-bold text-slate-800">{o.qty} pçs</div>
                            <div>{o.pallets} pal · {o.boxes} cx</div>
                          </div>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                            disabled={o.expedition_status === "invoiced" || o.expedition_status === "shipped"}
                            onClick={async () => {
                              if (!window.confirm(`Criar Guia de Venda para ${o.code}?`)) return;
                              try {
                                const r = await axios.post(`${API}/orders/${o.order_id}/quick-invoice`);
                                toast.success(r.data.message);
                                openZone("finished");
                              } catch (e) {
                                showError(e, "Erro a criar guia");
                              }
                            }}
                            data-testid={`quick-invoice-${o.code}`}
                          >
                            {o.expedition_status === "invoiced" ? "Já faturada" : "Faturar"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                }
              </div>
            </div>
          )}

          {zoneOpen === "shipping" && zoneData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Guias pendentes</div><div className="text-2xl font-bold text-amber-700">{zoneData.pending_guides}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Expedidas hoje</div><div className="text-2xl font-bold text-emerald-700">{zoneData.shipped_today}</div></CardContent></Card>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Guias de Venda Pendentes</h4>
                {zoneData.guides.length === 0 ? <p className="text-sm italic text-gray-500">Sem guias pendentes.</p> :
                  <div className="space-y-1">
                    {zoneData.guides.map((g, i) => (
                      <div key={i} className="flex justify-between items-center p-2 border rounded text-sm">
                        <div>
                          <div className="font-bold">{g.code}</div>
                          <div className="text-xs text-gray-500">{g.client_name}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <div className="font-bold">{g.total ? `${Number(g.total).toFixed(2)}€` : "-"}</div>
                            <div className="text-xs uppercase text-amber-700">{g.status}</div>
                          </div>
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white h-8"
                            onClick={async () => {
                              if (!window.confirm(`Marcar guia ${g.code} como expedida?`)) return;
                              try {
                                const r = await axios.post(`${API}/sales-guides/${g.guide_id}/mark-shipped`);
                                toast.success(r.data.message);
                                openZone("shipping");
                              } catch (e) {
                                showError(e, "Erro");
                              }
                            }}
                            data-testid={`ship-guide-${g.code}`}
                          >
                            Expedir
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                }
              </div>
            </div>
          )}
          {zoneOpen === "assembly" && zoneData && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Em Produção</div><div className="text-2xl font-bold text-emerald-700">{zoneData.in_progress}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Pendentes</div><div className="text-2xl font-bold text-amber-700">{zoneData.pending}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Total</div><div className="text-2xl font-bold">{zoneData.total_orders}</div></CardContent></Card>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Ordens de Montagem</h4>
                {zoneData.orders.length === 0 ? <p className="text-sm italic text-gray-500">Sem ordens de montagem.</p> :
                  <div className="space-y-1">
                    {zoneData.orders.map((o, i) => (
                      <div key={i} className="flex justify-between items-center p-2 border rounded text-sm hover:bg-slate-50">
                        <div>
                          <div className="font-bold">{o.code} <span className="text-xs text-gray-500 ml-2">{o.part_code}</span></div>
                          <div className="text-xs uppercase text-slate-500">{o.status}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right text-xs">
                            <div className="font-bold text-slate-800">{o.qty}/{o.target}</div>
                            <div className="text-emerald-600 font-semibold">{o.progress}%</div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => { setZoneOpen(null); navigate(`/orders?highlight=${o.code}`); }}
                            data-testid={`assembly-open-${o.code}`}
                          >
                            Abrir
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                }
              </div>
            </div>
          )}

          {zoneOpen === "offices" && zoneData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500">Utilizadores Online</div><div className="text-2xl font-bold text-emerald-700">{zoneData.online_count}</div></CardContent></Card>
                <Card><CardContent className="pt-4"><div className="text-xs text-gray-500 mb-1">Por Perfil</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(zoneData.by_role).map(([role, n]) => (
                      <span key={role} className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-700">{role}: {n}</span>
                    ))}
                  </div>
                </CardContent></Card>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Utilizadores Activos</h4>
                {zoneData.users.length === 0 ? <p className="text-sm italic text-gray-500">Ninguém online.</p> :
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {zoneData.users.map((u, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 border rounded hover:bg-slate-50">
                        {u.photo_url ? (
                          <img src={u.photo_url} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-slate-300 flex items-center justify-center font-bold text-slate-700">
                            {(u.name || u.user_code || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm truncate">{u.name || u.user_code}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <span className="uppercase font-bold">{u.role}</span>
                            <span>·</span>
                            <span>desde {u.clock_in_at ? new Date(u.clock_in_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" }) : "-"}</span>
                            {u.method === "qr" && <span className="text-blue-600 text-[10px] ml-1 font-bold">QR</span>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-blue-600"
                            title="Chat directo"
                            onClick={() => { setZoneOpen(null); navigate(`/messages?to=${u.user_code}`); }}
                            data-testid={`chat-user-${u.user_code}`}
                          >
                            💬
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-emerald-600"
                            title="Ver ficha de ponto"
                            onClick={() => { setZoneOpen(null); navigate(`/time-tracking?user=${u.user_code}`); }}
                            data-testid={`clock-user-${u.user_code}`}
                          >
                            🕒
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                }
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
